import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  ErrorDiagnosticData,
  buildDiagnosticData,
  saveDiagnosticToSession,
  logDiagnosticToConsole
} from './errorDiagnosticService';
import { ErrorDiagnosticView } from './ErrorDiagnosticView';

// Message/stack typiques de WebKit (iOS Safari) refusant l'ouverture d'un WebSocket dans un
// iframe cross-origin (mode EMBED) — reconnexion gérée en interne par le client Realtime Supabase.
function isIgnorableRealtimeSocketError(diagnostic: ErrorDiagnosticData): boolean {
  const message = diagnostic.message || '';
  const stack = diagnostic.stack || '';
  return (
    diagnostic.isEmbed &&
    /operation is insecure/i.test(message) &&
    /WebSocket|transportConnect|realtime/i.test(stack)
  );
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  diagnosticData: ErrorDiagnosticData | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  private static instance: GlobalErrorBoundary | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      diagnosticData: null
    };
  }

  static getDerivedStateFromError(error: any): Partial<State> {
    const diagnostic = buildDiagnosticData(error, 'React Error Boundary');
    return {
      hasError: true,
      diagnosticData: diagnostic
    };
  }

  componentDidCatch(error: any, errorInfo: ErrorInfo): void {
    const componentStack = errorInfo?.componentStack || undefined;
    const diagnostic = buildDiagnosticData(error, 'React Error Boundary', componentStack);

    saveDiagnosticToSession(diagnostic);
    logDiagnosticToConsole(diagnostic);

    this.setState({
      hasError: true,
      diagnosticData: diagnostic
    });
  }

  componentDidMount(): void {
    GlobalErrorBoundary.instance = this;

    // Attacher les écouteurs globaux pour capturer les erreurs hors du cycle de vie React
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

    // Exposer une fonction utilitaire sur window pour faciliter les tests de diagnostic
    if (typeof window !== 'undefined') {
      (window as any).__triggerDiagnosticTest = (type: 'react' | 'global' | 'promise' = 'react') => {
        if (type === 'promise') {
          Promise.reject(new Error('Test diagnostic: Rejet de promesse non géré (unhandledrejection)'));
        } else if (type === 'global') {
          setTimeout(() => {
            throw new Error('Test diagnostic: Erreur JavaScript globale asynchrone (window.onerror)');
          }, 10);
        } else {
          this.setState({
            hasError: true,
            diagnosticData: buildDiagnosticData(
              new Error('Test diagnostic: Erreur manuelle simulée dans React Error Boundary'),
              'Manuel'
            )
          });
        }
      };

      // Déclencheur via URL pour tester directement sur iPhone : /my-office?test_error=1
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('test_error') === '1') {
          setTimeout(() => {
            (window as any).__triggerDiagnosticTest('react');
          }, 500);
        }
      } catch {
        // Ignorer
      }
    }
  }

  componentWillUnmount(): void {
    if (GlobalErrorBoundary.instance === this) {
      GlobalErrorBoundary.instance = null;
    }
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  private handleGlobalError = (event: ErrorEvent): void => {
    // Éviter de capturer deux fois les erreurs déjà gérées par React
    const errorObj = event.error || event.message;
    const diagnostic = buildDiagnosticData(
      errorObj,
      'window.onerror',
      event.filename ? `Fichier: ${event.filename}:${event.lineno}:${event.colno}` : undefined
    );

    saveDiagnosticToSession(diagnostic);
    logDiagnosticToConsole(diagnostic);

    // Erreur non fatale : reconnexion WebSocket (chat temps réel) bloquée par Safari iOS en iframe
    // cross-origin (ITP intermittent) — le client Realtime retente seul, pas besoin de bloquer l'UI.
    if (isIgnorableRealtimeSocketError(diagnostic)) {
      return;
    }

    // Si nous n'avons pas déjà un écran d'erreur affiché, afficher le diagnostic
    if (!this.state.hasError) {
      this.setState({
        hasError: true,
        diagnosticData: diagnostic
      });
    }
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const errorObj = event.reason || 'Unhandled Promise Rejection';
    const diagnostic = buildDiagnosticData(errorObj, 'window.onunhandledrejection');

    saveDiagnosticToSession(diagnostic);
    logDiagnosticToConsole(diagnostic);

    if (!this.state.hasError) {
      this.setState({
        hasError: true,
        diagnosticData: diagnostic
      });
    }
  };

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      diagnosticData: null
    });
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.diagnosticData) {
      return (
        <ErrorDiagnosticView
          data={this.state.diagnosticData}
          onReload={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
